import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";

const SubscriptionTerms = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "instant", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Subscription Terms – PieFolio AI</title>
        <meta
          name="description"
          content="Billing, cancellation, refunds, and fair use terms for PieFolio AI Premium monthly and annual subscriptions and the Founding Lifetime purchase."
        />
        <link rel="canonical" href="https://getpiefolio.com/subscription-terms" />
        <meta property="og:title" content="Subscription Terms – PieFolio AI" />
        <meta
          property="og:description"
          content="Billing, cancellation, refunds, and fair use terms for PieFolio AI Premium subscriptions and the Founding Lifetime purchase."
        />
        <meta property="og:url" content="https://getpiefolio.com/subscription-terms" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "PieFolio AI Subscription Terms",
          url: "https://getpiefolio.com/subscription-terms",
          description:
            "Billing, cancellation, refunds, and fair use terms for PieFolio AI Premium subscriptions and the Founding Lifetime purchase.",
        })}</script>
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={logo} alt="PieFolio AI" className="h-8 w-auto" />
              <span className="font-bold text-xl">PieFolio AI</span>
            </Link>
            <Button asChild variant="ghost" size="sm">
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 sm:px-6 py-12 max-w-4xl">
        <div className="flex items-center gap-3 mb-8">
          <CreditCard className="w-8 h-8 text-primary" />
          <h1 className="text-3xl sm:text-4xl font-extrabold">Subscription Terms</h1>
        </div>

        <p className="text-muted-foreground mb-8">Last updated: 14 October 2026</p>

        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4">1. Premium Subscription Overview</h2>
            <p className="text-muted-foreground leading-relaxed">
              PieFolio AI Premium is one access level offered in three ways through the Apple App Store and Google Play:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
              <li><strong>Premium Monthly</strong> — auto-renewing, billed monthly.</li>
              <li><strong>Premium Annual</strong> — auto-renewing, billed every 12 months.</li>
              <li><strong>Founding Lifetime</strong> — a one-time, non-renewing purchase.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              The exact price, currency, and any free trial or introductory offer are displayed in the app at the point
              of purchase and are localised by Apple and Google for your store country. Premium unlocks the full set of
              paid features described on the in-app paywall.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Auto-Renewal</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              This section applies to Premium Monthly and Premium Annual. The Founding Lifetime does not renew.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Payment will be charged to your <strong>Apple ID</strong> or <strong>Google Play</strong> account at confirmation of purchase.</li>
              <li>Your subscription <strong>automatically renews</strong> for the same period and at the then-current price unless auto-renewal is turned off at least <strong>24 hours before the end of the current period</strong>.</li>
              <li>Your account will be charged for renewal within <strong>24 hours prior to the end of the current period</strong>.</li>
              <li>You can <strong>manage your subscription and turn off auto-renewal</strong> at any time after purchase from your Apple ID or Google Play account settings.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. Free Trials & Introductory Offers</h2>
            <p className="text-muted-foreground leading-relaxed">
              The free trial is 7 days and is offered on Premium Annual only, and only when Apple or Google confirm
              that your account is eligible. Trial and introductory offer terms are shown before you confirm the
              purchase. If you do not cancel at least 24 hours before the trial ends, your subscription will
              automatically convert to a paid subscription at the standard rate. In line with Apple's policy, any unused
              portion of a free trial is forfeited when you purchase a subscription where applicable.
            </p>
          </section>

          <section id="lifetime" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4">4. Founding Lifetime</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>One-time purchase:</strong> Founding Lifetime is a one-time, non-renewing in-app purchase through Apple or Google. There is no auto-renewal and nothing to cancel.</li>
              <li><strong>Meaning of “for life” or “lifetime”:</strong> Access to Premium, as Premium is offered at the time of purchase, for as long as PieFolio AI operates the PieFolio AI app and its Premium service. It is not tied to the lifetime of the buyer. It is personal to the Apple ID or Google account used for the purchase and cannot be transferred.</li>
              <li><strong>Limited offer:</strong> The founding lifetime price is limited to the first 30 lifetime purchases, and lifetime purchases are capped at 100 in total. After that, the offer may end or a different price may apply.</li>
              <li><strong>Included features:</strong> Automatic broker sync is not included. AI features are subject to the <Link to="/terms#fair-use" className="text-primary hover:underline">Fair use limits in the Terms of Service</Link>.</li>
              <li><strong>Refunds:</strong> The purchase is non-refundable through PieFolio AI. Refund requests are handled only by Apple or Google under their policies; see <Link to="#refunds" className="text-primary hover:underline">section 6, Refunds</Link>. Mandatory consumer rights under applicable law remain unaffected.</li>
              <li><strong>Switching from a subscription:</strong> Buying Founding Lifetime does <strong>not</strong> cancel an existing monthly or annual subscription automatically. You must cancel the subscription yourself in your Apple ID or Google Play settings; see <Link to="#cancellation" className="text-primary hover:underline">section 5, Cancellation</Link>. Otherwise it continues to renew.</li>
              <li><strong>Restore:</strong> You can restore the lifetime purchase from the in-app Plans screen with the same Apple ID or Google account used for the purchase.</li>
              <li><strong>Family Sharing:</strong> Founding Lifetime is not enabled for Family Sharing.</li>
            </ul>
          </section>

          <section id="cancellation" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4">5. Cancellation</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              This section applies to monthly and annual subscriptions; Founding Lifetime does not renew and has nothing to cancel.{" "}
              You can cancel at any time. Cancelling stops the next renewal, and your Premium access continues until the
              end of the current billing period.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>iOS:</strong> Settings → tap your name → Subscriptions → PieFolio AI → Cancel Subscription. See <a className="text-primary hover:underline" href="https://support.apple.com/HT202039" target="_blank" rel="noopener noreferrer">Apple's instructions</a>.</li>
              <li><strong>Android:</strong> Open the Google Play Store → Profile → Payments &amp; subscriptions → Subscriptions → PieFolio AI → Cancel. See <a className="text-primary hover:underline" href="https://support.google.com/googleplay/answer/7018481" target="_blank" rel="noopener noreferrer">Google's instructions</a>.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Deleting the PieFolio AI app does not cancel your subscription — you must cancel it from your Apple ID or
              Google Play account.
            </p>
          </section>

          <section id="refunds" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4">6. Refunds</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              All payments for Premium subscriptions and the Founding Lifetime purchase are processed by Apple or Google. Refund requests are handled
              under their respective policies:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Apple App Store:</strong> <a className="text-primary hover:underline" href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer">reportaproblem.apple.com</a>.</li>
              <li><strong>Google Play:</strong> <a className="text-primary hover:underline" href="https://support.google.com/googleplay/answer/2479637" target="_blank" rel="noopener noreferrer">Google Play refund policy</a>.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong>EU/EEA/UK consumers:</strong> You normally have a 14-day statutory right of withdrawal for
              distance contracts for digital content. By starting to use the Premium features immediately after
              purchase, you expressly request immediate performance and acknowledge that you lose your right of
              withdrawal once delivery of the digital content has begun, to the extent permitted by applicable law.
              Non-waivable consumer rights under your local law remain unaffected.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">7. Price Changes</h2>
            <p className="text-muted-foreground leading-relaxed">
              This section applies to monthly and annual subscriptions; Founding Lifetime does not renew and has no price changes after purchase.{" "}
              We may change subscription prices. Where required, Apple or Google will notify you of price increases and
              ask you to consent before the new price applies. Continued use of the Premium Subscription after a price
              change takes effect constitutes your acceptance of the new price.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">8. Restoring Purchases</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you reinstall PieFolio AI or sign in on a new device, you can restore your active subscription or
              lifetime purchase from the in-app Plans screen using the same Apple ID or Google account used for the original purchase.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">9. Family Sharing</h2>
            <p className="text-muted-foreground leading-relaxed">
              All PieFolio AI Premium options, including Founding Lifetime, are currently <strong>not enabled for Apple Family Sharing or Google
              Play family sharing</strong>. Each user account requires its own subscription or lifetime purchase.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">10. Fair use</h2>
            <p className="text-muted-foreground leading-relaxed">
              AI features on all Premium options are subject to the <Link to="/terms#fair-use" className="text-primary hover:underline">Fair use section of the Terms of Service</Link>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">11. Apple App Store Additional Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              The PieFolio AI iOS app is licensed (not sold) to you under Apple's standard <a className="text-primary hover:underline" href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer">Licensed Application End User License Agreement</a>.
              You acknowledge that this agreement is between you and PieFolio AI only, and that <strong>Apple is not a
              party to it</strong>. Apple is not responsible for the app or its content and has no warranty, support,
              product-liability, or intellectual-property obligations with respect to the app. Apple and its
              subsidiaries are third-party beneficiaries of these terms and may enforce them against you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">12. Google Play Additional Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              Purchases made through Google Play are also subject to the <a className="text-primary hover:underline" href="https://play.google.com/intl/en/about/play-terms/" target="_blank" rel="noopener noreferrer">Google Play Terms of Service</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">13. Relationship to Other Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Subscription Terms supplement our <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link> and
              {" "}<Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>. In the event of a
              conflict regarding billing, auto-renewal, cancellation, or refunds, these Subscription Terms control.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">14. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              Questions about your subscription or lifetime purchase? Email{" "}
              <a href="mailto:contact@getpiefolio.com" className="text-primary hover:underline">contact@getpiefolio.com</a>.
              For billing disputes, please also contact Apple or Google directly using the links in Section 6.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} PieFolio AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default SubscriptionTerms;
